<template>
    <base-dialog :value="dialog" :width="560" :title="$t('common.admin_teams_groups')"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="admin-tabs">
            <select-option-students only-groups @change="currentGroupBy = $event" />
        </div>

        <div class="admin-table-header">
            <div class="admin-th">{{ $t('common.name') }}</div>
            <div class="admin-th">{{ $t('common.students') }}</div>
            <div></div>
        </div>

        <div v-if="items.length === 0" class="admin-empty">{{ $t('common.not_found') }}</div>
        <div v-for="item in items" :key="item.id" class="admin-row">
            <span class="admin-row-name">{{ item.name }}</span>
            <span class="admin-row-count">{{ item.quantityStudents }}</span>
            <div class="admin-row-actions">
                <button class="admin-action-btn" @click="editItem(item)">
                    <v-icon size="14">mdi-pencil-outline</v-icon>
                </button>
                <button class="admin-action-btn del" @click="showDeleteConfirm(item)">
                    <v-icon size="14">mdi-trash-can-outline</v-icon>
                </button>
            </div>
        </div>

        <kimsa-confirm ref="delete" :title="$t('common.delete_group')" :content="$t('common.delete_group_subtext')"
            :loading="loadingDelete" :persistent="false" @confirm="onConfirmDelete"
            @cancel="currentSelection = null" :close-on-confirm="false" :alert-mode="true" />
        <create-group-students-dialog is-second-dialog ref="createGroup" />
        <create-team-students-dialog is-second-dialog ref="createTeam" />

        <template v-slot:footer>
            <v-btn block depressed x-large color="primary" class="footer-btn black--text" @click="hide">
                {{ $t('common.close') }}
            </v-btn>
        </template>
    </base-dialog>
</template>

<script>
import { mapState } from "vuex";
import {db, FieldValue} from "@/utils/Firebase";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import CreateGroupStudentsDialog from "@/components/coach/CreateGroupStudentsDialog";
import CreateTeamStudentsDialog from "@/components/coach/CreateTeamStudentsDialog";
import SelectOptionStudents from "@/components/coach/SelectOptionStudents";
import {deleteAssignedRoutinesGroupCollection, deleteGroupTeam, removeStudentsFromGroup} from "@/utils/Exercises";
import BaseDialog from "@/components/common/BaseDialog";

export default {
    name: "AdminGroupsDialog",
    components: {
        SelectOptionStudents,
        CreateTeamStudentsDialog, CreateGroupStudentsDialog, KimsaConfirm, BaseDialog
    },
    data() {
        return {
            dialog: false,
            currentGroupBy: 'group',
            loadingDelete: false,
            currentSelection: null,
        }
    },
    computed: {
        ...mapState(['groups', 'teams']),
        items(vm = this) {
            let items
            if (vm.currentGroupBy === 'group') {
                items = vm.groups.map(group => ({
                    id: group.id,
                    name: group.name,
                    quantityStudents: group.students.length || 0,
                    students: group.students,
                }))
            } else {
                items = vm.teams.map(team => {
                    let quantityStudents = 0
                    team.groups.forEach(group => {
                        quantityStudents += group.students.length || 0
                    })
                    return {
                        id: team.id,
                        name: team.name,
                        groups: team.groups,
                        quantityStudents: quantityStudents,
                    }
                })
            }
            return items.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        }
    },
    methods: {
        show() {
            const vm = this
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
        },
        async onConfirmDelete() {
            const vm = this
            await vm.withLoading(async () => {
                if (vm.currentGroupBy === 'group') {
                    await deleteAssignedRoutinesGroupCollection(vm.currentSelection.id)
                    await removeStudentsFromGroup(vm.currentSelection.students || [])
                    await deleteGroupTeam(vm.currentSelection.id, 'groups')

                    await db.collection('users').doc(vm.currentUser.uid).update({
                        groupsQuantity: FieldValue.increment(-1),
                    })
                } else {
                    for (let group of vm.currentSelection.groups) {
                        await deleteAssignedRoutinesGroupCollection(group.id, vm.currentSelection.id)
                        await removeStudentsFromGroup(group.students || [])
                    }
                    await deleteGroupTeam(vm.currentSelection.id, 'teams')

                    await db.collection('users').doc(vm.currentUser.uid).update({
                        teamsQuantity: FieldValue.increment(-1),
                    })
                }

                vm.$refs.delete?.hide()
            }, 'loadingDelete')
        },
        showDeleteConfirm(currentSelection) {
            const vm = this
            vm.currentSelection = currentSelection
            vm.$refs.delete.show()
        },
        editItem(currentSelection) {
            const vm = this
            if (vm.currentGroupBy === 'group') return vm.$refs.createGroup.loadGroup(currentSelection)
            return vm.$refs.createTeam.loadTeam(currentSelection)
        },
    },
}
</script>

<style scoped>
.admin-tabs {
    padding-bottom: 16px;
    border-bottom: 1px solid var(--v-outline-base);
    margin-bottom: 16px;
}

.admin-table-header {
    display: grid;
    grid-template-columns: 1fr 80px 72px;
    padding: 0 8px 8px;
    gap: 12px;
}

.admin-th {
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.admin-empty {
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
    padding: 24px 0;
}

.admin-row {
    display: grid;
    grid-template-columns: 1fr 80px 72px;
    align-items: center;
    padding: 11px 8px;
    border-top: 1px solid var(--v-outline-base);
    gap: 12px;
    border-radius: 6px;
    transition: background 0.1s;
}

.admin-row:hover {
    background: var(--v-surface2-base);
}

.admin-row-name {
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
}

.admin-row-count {
    font-size: 13px;
    color: var(--v-txtSecondary-base);
}

.admin-row-actions {
    display: flex;
    gap: 4px;
    justify-content: flex-end;
}

.admin-action-btn {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline2-base);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-txtMuted-base);
    transition: all 0.12s;
}

.admin-action-btn .v-icon {
    color: inherit;
}

.admin-action-btn:hover {
    color: var(--v-txtPrimary-base);
    background: var(--v-outline2-base);
}

.admin-action-btn.del:hover {
    color: var(--v-error-base);
    border-color: var(--v-error-base);
    background: rgba(232, 92, 92, 0.08);
}

.footer-btn {
    border-radius: 0 !important;
    height: 52px !important;
    font-weight: 700;
}
</style>
